import React, { createRef, useCallback, useContext, useEffect, useState } from "react";
import { Box } from "@motorata/base-ui.components.atoms._atoms";
import RadioSlider from "@motorata/base-ui.components.atoms.radio-slider";
import { useForm } from "react-hook-form";
import { AnalyticsService } from "@motorata/base-ui.services.segment-service";
import { SentryService } from "@motorata/base-ui.services.sentry-service";
import Alert from "@motorata/base-ui.components.molecules.alert";
import Input from "@motorata/base-ui.components.atoms.input";
import Button from "@motorata/base-ui.components.atoms.button";
import {
  CONFIRM_CODE_REGEX,
  EMAIL_REGEX,
  IS_ONE_DIGIT,
  PHONE_STRING_REGEX,
  testRegex
} from "@motorata/base-ui.utils.regex";
import Checkbox from "@motorata/base-ui.components.atoms.checkbox";
import { useRouter } from "next/navigation";
import Modal from "@motorata/base-ui.components.molecules.modal";
import { P } from "@motorata/base-ui.components.atoms.typography";
import styled from "styled-components";
import { AuthService } from "@/services/AuthService";
import { setToken } from "@/services/TokenServce";
import { AccountContext } from "@/providers/AccountProvider";

const VerifyContent = ({ setShow }) => {
  const arrLength = 4;

  const router = useRouter()
  const { account, setAccount, setState : setAccountState } = useContext(AccountContext);

  const [elRefs, setElRefs] = useState([]);

  const [state, setState] = useState({
    1: "",
    2: "",
    3: "",
    4: ""
  });
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)

  const onChange = (e, number) => {
    const value = e.target.value;
    if (value.length === 1 && testRegex(value, IS_ONE_DIGIT)) {
      setState({
        ...state,
        [`${number}`]: value
      });
      if (number < 4) {
        elRefs[number].current.focus();
      }
    } else if (value.length === 4 && testRegex(value, CONFIRM_CODE_REGEX)) {
      setState({
        1: value[0],
        2: value[1],
        3: value[2],
        4: value[3],
      })
    } else if (value.length === 0) {
      setState({
        ...state,
        [`${number}`]: ""
      });
    }
  };

  const onSubmit = useCallback(async () => {
    try {
      setLoading(true)
      await AuthService.verifyCode(`${state["1"]}${state["2"]}${state["3"]}${state["4"]}`)
      setShow(false)
      setAccountState("account")
      router.push("/konto")
    } catch (e) {
      setError(true)
      SentryService.error("[ERROR AuthService.verify API]", e)
    } finally {
      setLoading(false)
    }
  }, [state])

  const resendCode = useCallback(async () => {
    try {
      await AuthService.resendCode()
    } catch (e) {
      SentryService.error("[ERROR AuthService.resendCode API]", e)
    }
  }, [state])

  useEffect(() => {
    if (elRefs[0]) {
      elRefs[0].current?.focus();
    }
  }, [elRefs]);

  useEffect(() => {
    setElRefs([createRef(), createRef(), createRef(), createRef()]);
  }, []);

  return (
    <Box>
    <P small textAlign="left">
      Wprowadź otrzymany kod SMS, który wysłaliśmy na numer telefonu <b>{account?.phone}</b>.
    </P>
    {error ? <Alert type="error" small>Niepoprawny kod weryfikacyjny</Alert> : <></>}
    <CodeWrapper>
      <input ref={elRefs[0]} value={state["1"]} onChange={e => onChange(e, 1)} />
      <input ref={elRefs[1]} value={state["2"]} onChange={e => onChange(e, 2)} />
      <input ref={elRefs[2]} value={state["3"]} onChange={e => onChange(e, 3)} />
      <input ref={elRefs[3]} value={state["4"]} onChange={e => onChange(e, 4)} />
    </CodeWrapper>
    <P small>
      Aby otrzymać ponownie kod <button onClick={resendCode}>kliknij tutaj</button>.
    </P>
    <Button loading={loading} onClick={onSubmit} submit width="100%">
      Zweryfikuj
    </Button>
    <Button width="100%" type="third" onClick={() => setAccountState('register')}>
      Wróć
    </Button>
    </Box>
  )
}

const LoginContent = ({ setShow }) => {
  const router = useRouter()
  const { setHasToken, setAccount, setState } = useContext(AccountContext);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm()

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)

  const onSubmit = useCallback(async e => {
    try {
      setLoading(true)
      setError(false)
      const res = await AuthService.login(e)
      if (res.role === "ROLE_USER") {
        setToken(res?.token)
        setHasToken(true)
        if(res.status === "active"){
          setState('account')
          router.push("/konto")
          setShow(false)
        }else {
          setState('verify')
        }
        AnalyticsService.track("Login")
      } else {
        setError(true)
      }
    } catch (e) {
      setError(true)
      SentryService.error("[ERROR AuthService.login API]", e)
    } finally {
      setLoading(false)
    }
  }, [])

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      {error && <Alert type="error" small>Nie udało się zalogować - nie właściwy email lub hasło.</Alert>}
      <Input
        name="name"
        label="Nazwa użytkownika"
        error={errors.username}
        value={watch('username')}
        marginBottom="XS"
        rhf={register("username", {
          required: true,
        })}
      />
      <Input
        name="password"
        label="Hasło"
        type="password"
        error={errors.password}
        value={watch('password')}
        marginBottom="S"
        rhf={register("password", {
          required: true,
        })}
      />
      <Button width="100%" submit loading={loading}>
        Zaloguj się
      </Button>
    </form>
  )
}

const RegisterContent = () => {
  const { setHasToken, setState, account } = useContext(AccountContext);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm()

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)

  const checkbox = watch("checkbox");

  const onSubmit = useCallback(async e => {
    try {
      setLoading(true)
      const res = await AuthService.register(e)
      setToken(res?.token)
      setHasToken(true)
      setState('verify')
      AnalyticsService.track("Register")
    } catch (e) {
      setError(true)
      SentryService.error("[ERROR AuthService.register API]", e)
    } finally {
      setLoading(false)
    }
  }, [])

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      {error && <Alert type="error" small>Podany email jest już zajęty - spróbuj podać inne dane.</Alert>}
      <Input
        name="username"
        label="E-mail*"
        error={errors.username}
        value={watch('username')}
        marginBottom="XS"
        rhf={register("username", {
          required: true,
          pattern: {
            value: EMAIL_REGEX,
            message: "Niepoprawny format email"
          }
        })}
      />
      <Input
        name="firstName"
        label="Imię*"
        error={errors.firstName}
        value={watch('firstName')}
        marginBottom="XS"
        rhf={register("firstName", {
          required: true,
        })}
      />
      <Input
        name="lastName"
        label="Nazwisko*"
        error={errors.lastName}
        value={watch('lastName')}
        marginBottom="XS"
        rhf={register("lastName", {
          required: true,
        })}
      />
      <Input
        name="phone"
        label="Telefon*"
        error={errors.phone}
        elementType="pattern"
        format="+48 ### ### ###"
        formattedValue={true}
        value={watch('phone')}
        rhf={register("phone", {
          required: true,
          pattern: {
            value: PHONE_STRING_REGEX,
            message: "Niepoprawny format telefonu. +48 123 456 789"
          }
        })}
        marginBottom="XS"
      />
      <Input
        name="password"
        label="Hasło*"
        type="password"
        error={errors.password}
        value={watch('password')}
        marginBottom="XS"
        rhf={register("password", {
          required: true,
        })}
      />
      <Checkbox
        name="checkbox"
        value={checkbox}
        error={errors.checkbox}
        rhf={register("checkbox", {
          required: true,
        })}
      >
        *Oświadczam, że zapoznałem/-am się z <a href="/regulamin">Regulaminem</a> i <a href="/polityka-prywatnosci">Polityką prywatności</a> oraz akceptuję ich postanowienia.
      </Checkbox>
      <Button submit width="100%" mt="S" loading={loading}>
        Zarejestruj się
      </Button>
      {/*<Link type="secondary" width="100%" mt="S" href="https://panel.motorata.pl/register">*/}
      {/*  Utwórz konto biznesowe*/}
      {/*</Link>*/}
    </form>
  )
}

const LoginRegisterModal = ({ modal, setModal }) => {
  const { state, setState } = useContext(AccountContext);

  if(state === "account") {
    return <></>
  }

  if(state === "verify") {
    return <Modal
      title="Potwierdz swój numer telefonu"
      type="center"
      maxWidth="400px"
      show={modal}
      setShow={setModal}
    >
      <VerifyContent setShow={setModal} />
    </Modal>
  }

  return (
    <Modal
      title="Twoje konto"
      type="center"
      maxWidth="400px"
      show={modal}
      setShow={setModal}
    >
      <Box>
        <RadioSlider
          name="productType"
          background="backgroundDarker"
          width="100%"
          value={state}
          onChange={({ value }) => setState(value)}
          options={[
            ["Zaloguj się", "login"],
            ["Zarejestruj się", "register"],
          ]}
          columnBreakpoint={500}
          marginBottom="S"
        />
        {state === "login" && <LoginContent setShow={setModal} />}
        {state === "register" && <RegisterContent />}
      </Box>
    </Modal>
  );
};

export default LoginRegisterModal

const CodeWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin: ${({ theme }) => theme.space.M} 0;

  input {
    background: ${({ theme }) => theme.color.background};
    color: ${({ theme }) => theme.color.secondary};
    font-weight: ${({ theme }) => theme.font.normal};
    width: 40px;
    height: 65px;
    border-radius: 3px;
    border: none;
    margin: 0 5px;
    text-align: center;
    font-size: 30px;
    padding: 0 !important;
  }
`;
